import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadDocument, fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge, fmtDate, fmtDateTime } from "@/components/badges";
import { Download, Eye, Pencil, Plus, Trash2, Upload, XCircle } from "lucide-react";
import DocumentViewer from "@/components/DocumentViewer";
import { useTranslation } from "react-i18next";
import { CreateRequestDialog } from "@/components/filerequests/CreateRequestDialog";
import { ViewRequestDialog } from "@/components/filerequests/ViewRequestDialog";
import { FulfillRequestDialog } from "@/components/filerequests/FulfillRequestDialog";
import { EditRequestDialog } from "@/components/filerequests/EditRequestDialog";
import { CancelRequestDialog } from "@/components/filerequests/CancelRequestDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export default function FileRequests() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { selectedId } = useProject();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [fulfillReq, setFulfillReq] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewReq, setViewReq] = useState(null);
  const [editReq, setEditReq] = useState(null);
  const [cancelReq, setCancelReq] = useState(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["file-requests", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/file-requests`)).data,
    enabled: !!selectedId,
  });
  const { data: documents = [] } = useQuery({
    queryKey: ["documents", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/documents`)).data,
    enabled: !!selectedId,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["parties", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/parties`)).data,
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["file-requests", selectedId] });
    qc.invalidateQueries({ queryKey: ["documents", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };

  const deleteReqMut = useMutation({
    mutationFn: async (id) => api.delete(`/file-requests/${id}`),
    onSuccess: () => {
      toast.success(t("filesPage.toastDeleted"));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const filtered = useMemo(() => {
    if (filter === "all") return requests;
    if (filter === "overdue") return requests.filter((r) => r.overdue);
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  return (
    <div className="space-y-6" data-testid="files-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{t("filesPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("filesPage.subtitle")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-file-request-button">
          <Plus className="mr-2 h-4 w-4" /> {t("filesPage.newRequest")}
        </Button>
      </div>

      <Tabs defaultValue="requests">
        <TabsList data-testid="files-tabs">
          <TabsTrigger value="requests" data-testid="tab-requests">{t("filesPage.tabRequests")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">{t("filesPage.tabDocuments")}</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="request-filters">
            {["all", "pending", "fulfilled", "overdue"].map((f) => (
              <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} className={filter === f ? "bg-slate-900" : ""} data-testid={`request-filter-${f}`}>
                {t(`filesPage.filter${f[0].toUpperCase() + f.slice(1)}`)}
              </Button>
            ))}
          </div>
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("filesPage.thRequest")}</TableHead>
                    <TableHead>{t("common.category")}</TableHead>
                    <TableHead>{t("filesPage.thFromTo")}</TableHead>
                    <TableHead>{t("dashboard.due")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="text-right">{t("filesPage.thAction")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500">{t("common.loading")}</TableCell></TableRow>}
                  {!isLoading && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500" data-testid="requests-empty">{t("filesPage.empty")}</TableCell></TableRow>
                  )}
                  {filtered.map((r) => (
                    <TableRow key={r.id} className={r.overdue ? "bg-rose-50/50" : ""} data-testid={`file-request-row-${r.id}`}>
                      <TableCell className="max-w-md">
                        <button type="button" className="text-left font-medium text-slate-900 transition-colors hover:text-sky-700 hover:underline" onClick={() => setViewReq(r)} data-testid={`file-request-title-${r.id}`}>
                          {r.title}
                        </button>
                        {r.description && (
                          <div className="line-clamp-1 text-xs text-slate-500" data-testid={`file-request-preview-${r.id}`}>
                            {r.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{r.category}</Badge></TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{r.requester?.company_name}</span>
                        <span className="mx-1 text-slate-400">→</span>
                        <span className="font-medium">{r.target?.company_name}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-mono text-xs ${r.overdue ? "font-bold text-rose-700" : "text-slate-600"}`}>{fmtDate(r.due_date)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={r.status} data-testid={`file-request-status-${r.id}`} />
                          {r.overdue && <StatusBadge status="overdue" data-testid={`file-request-overdue-${r.id}`} />}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title={t("common.view")} onClick={() => setViewReq(r)} data-testid={`file-request-view-${r.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.can_edit && (
                            <Button variant="ghost" size="icon" title={t("filesPage.editRequestT")} onClick={() => setEditReq(r)} data-testid={`file-request-edit-${r.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {r.can_cancel && (
                            <Button variant="ghost" size="icon" title={t("filesPage.cancelRequestT")} className="text-amber-700" onClick={() => setCancelReq(r)} data-testid={`file-request-cancel-${r.id}`}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {r.can_delete && (
                            <Button variant="ghost" size="icon" title={t("filesPage.deleteRequestT")} className="text-rose-600" onClick={() => askConfirm(t("filesPage.deleteConfirm", { title: r.title }), () => deleteReqMut.mutate(r.id))} data-testid={`file-request-delete-${r.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {r.status === "pending" && r.can_fulfill && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setFulfillReq(r)}
                              data-testid={`file-request-fulfill-${r.id}`}
                            >
                              <Upload className="mr-1.5 h-3.5 w-3.5" /> {t("filesPage.uploadFile")}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.file")}</TableHead>
                    <TableHead>{t("common.category")}</TableHead>
                    <TableHead>{t("common.version")}</TableHead>
                    <TableHead>{t("progress.party")}</TableHead>
                    <TableHead>{t("common.uploaded")}</TableHead>
                    <TableHead className="text-right">{t("library.downloadT")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500" data-testid="documents-empty">{t("filesPage.docsEmpty")}</TableCell></TableRow>
                  )}
                  {documents.map((d) => (
                    <TableRow key={d.id} className={d.is_latest === false ? "opacity-55" : ""} data-testid={`document-row-${d.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{d.filename}</div>
                        {d.note && <div className="text-xs text-slate-500">{d.note}</div>}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{d.category}</Badge></TableCell>
                      <TableCell>
                        <span className="font-mono text-xs font-semibold" data-testid={`document-version-${d.id}`}>{d.version}</span>
                        {d.version_count > 1 && <Badge variant="outline" className="ml-1.5 font-mono text-[10px]">{d.version_count} {t("filesPage.revSuffix")}</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{d.party?.company_name || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500">{d.uploaded_by_email} · {fmtDateTime(d.uploaded_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setViewerDoc(d)} data-testid={`document-view-${d.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => downloadDocument(d.id, d.filename)} data-testid={`document-download-${d.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateRequestDialog open={createOpen} onOpenChange={setCreateOpen} parties={parties} />
      <ViewRequestDialog req={viewReq} onClose={() => setViewReq(null)} />
      <FulfillRequestDialog req={fulfillReq} onClose={() => setFulfillReq(null)} />
      <EditRequestDialog req={editReq} onClose={() => setEditReq(null)} />
      <CancelRequestDialog req={cancelReq} onClose={() => setCancelReq(null)} />

      <DocumentViewer doc={viewerDoc} open={!!viewerDoc} onOpenChange={(o) => !o && setViewerDoc(null)} />
      {confirmDialog}
    </div>
  );
}
